import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Stack,
  Typography,
  Alert,
  Chip,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fetchPaymentFailedSmsPreview } from '../api/members';
import { colors } from '../design-system/tokens';

/**
 * 결제 실패 안내 문자 — dry-run 미리보기 모달.
 *
 * 이 화면은 **실제로 발송하지 않는다.** 누구에게 어떤 문구가 나갈지 보여주기만 한다.
 * 발송 버튼은 아직 없다 (실 발송은 매장 플래그·전역 킬스위치를 켠 뒤 다음 슬라이스).
 */

const MODE_BANNER = {
  dry_run: {
    severity: 'info',
    text: '준비 모드예요. 아래는 미리보기이며 실제로 문자가 발송되지 않습니다.',
  },
  blocked_qa: {
    severity: 'warning',
    text: 'QA 모드에서는 발송할 수 없어요. 미리보기만 가능합니다.',
  },
  live: {
    severity: 'success',
    text: '이 매장은 발송이 켜져 있어요. (실제 발송 기능은 다음 단계에서 연결됩니다)',
  },
};

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

const PaymentFailedSmsPreview = ({ open, session, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchPaymentFailedSmsPreview(getScopedParams(session)));
    } catch (err) {
      setError(err?.response?.data?.message || '미리보기를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const banner = data ? MODE_BANNER[data.action?.mode] ?? MODE_BANNER.dry_run : null;
  // 실발송 시 실제로 나갈 인원 — backend 계산값(sendable)을 신뢰하고, 구버전 응답이면 계산
  const sendable = data
    ? (data.sendable ?? data.totalRecipients - data.undeliverable)
    : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
        결제 실패 안내 문자 미리보기
        <IconButton
          onClick={onClose}
          aria-label="닫기"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={load}>
                다시 시도
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            {banner && (
              <Alert severity={banner.severity}>
                {banner.text}
              </Alert>
            )}

            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
                  발송 대상
                </Typography>
                <Typography variant="h6">{sendable}명</Typography>
              </Box>
              {data.undeliverable > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
                    연락처 없음
                  </Typography>
                  <Typography variant="h6" sx={{ color: colors.status.warning.fg }}>
                    {data.undeliverable}명
                  </Typography>
                </Box>
              )}
              {data.onCooldown > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
                    최근 발송됨 (제외)
                  </Typography>
                  <Typography variant="h6" sx={{ color: colors.status.info.fg }}>
                    {data.onCooldown}명
                  </Typography>
                </Box>
              )}
            </Stack>

            {data.onCooldown > 0 && (
              <Typography variant="caption" sx={{ color: colors.text.muted }}>
                같은 회원에게는 {data.cooldownHours ?? 24}시간에 한 번만 발송돼요. 최근에 이미
                받은 회원은 자동으로 제외됩니다.
              </Typography>
            )}

            <Box>
              <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
                문자 템플릿
              </Typography>
              <Paper
                variant="outlined"
                sx={{ mt: 0.75, p: 1.5, bgcolor: colors.canvas.subtle, borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  {data.template}
                </Typography>
              </Paper>
              <Typography variant="caption" sx={{ color: colors.text.muted }}>
                문구는 승인된 템플릿이며 변수만 자동으로 채워집니다.
              </Typography>
            </Box>

            {data.recipients.length > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
                  미리보기 (상위 {data.recipients.length}명)
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mt: 0.75, borderRadius: 1, overflowX: 'auto' }}
                >
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>회원</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>연락처</TableCell>
                        <TableCell>발송 문구</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recipients.map((r) => (
                        <TableRow key={r.memberId}>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                            {r.name}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {r.deliverable ? (
                              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                <span>{r.phoneMasked}</span>
                                {r.onCooldown && (
                                  <Chip
                                    size="small"
                                    label="최근 발송됨"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      bgcolor: colors.status.info.bg,
                                      color: colors.status.info.fg,
                                    }}
                                  />
                                )}
                              </Stack>
                            ) : (
                              <Chip
                                size="small"
                                label="연락처 없음"
                                sx={{
                                  height: 20,
                                  fontSize: 11,
                                  bgcolor: colors.status.warning.bg,
                                  color: colors.status.warning.fg,
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ color: colors.text.secondary }}>
                            {r.message}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="success">
                결제 실패 회원이 없어요. 보낼 안내가 없습니다.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>닫기</Button>
        {/*
          발송 버튼은 아직 없다. 실 발송은 매장 플래그 + 전역 킬스위치를 켠 뒤
          다음 슬라이스에서 붙인다. 지금 누를 수 있으면 dry-run 원칙이 깨진다.
        */}
        <Button variant="contained" disabled>
          발송 (준비 중)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentFailedSmsPreview;
